import { useState, useEffect, useMemo } from 'react';
import { useNavigation } from '../../contexts/NavigationContext';
import { useSession } from '../../contexts/SessionContext';
import { getConnections } from '../../services/storage/connectionStorage';
import { loadLatestMessages } from '../../services/chat/messageStore';
import { formatRelativeTime } from '../../utils/dateHelpers';
import { formatLocationForCard } from '../../utils/locationFormatters';
import { getSenderId } from '../../utils/identityUtils';
import {
  hasUnreadMessages,
  getTime,
  deriveLatestMessage,
  hasChatMessages,
  formatMessagePreview,
} from '../../utils/chatHelpers';
import SwipeableConnectionCard from '../../components/UI/Cards/SwipeableConnectionCard';

/**
 * AnonChatListPage Component
 *
 * Dedicated Chats tab for Anon users. Shows connections with chat messages,
 * sorted by most recent message.
 */
const AnonChatListPage = () => {
  const { navigateTo } = useNavigation();
  const { updateSessionChatRoomId } = useSession();

  const [connections, setConnections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // "No chats yet" is only trustworthy after the latest-messages enrichment
  // settles — rooms with no local message history can't qualify before it.
  const [enriched, setEnriched] = useState(false);
  const [enrichFailed, setEnrichFailed] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Filter to chat connections and sort by recency
  const chatConnections = useMemo(() => {
    const result = connections.filter(
      (c) => hasChatMessages(c.chatMessages) || c.latestMessage?.type === 'chat'
    );
    result.sort(
      (a, b) => getTime(b.latestMessage?.timestamp) - getTime(a.latestMessage?.timestamp)
    );
    return result;
  }, [connections]);

  // Load connections from localStorage
  const loadConnectionsFromStorage = async () => {
    const allConnections = await getConnections();
    if (!allConnections || !allConnections.length) {
      setConnections([]);
      setIsLoading(false);
      return [];
    }

    const validConnections = allConnections.map((conn) => ({
      chatRoomId: conn.chatRoomId,
      profileData: conn.profileData || {},
      anonData: conn.profileData || {},
      connectionMetadata: conn.connectionMetadata || {},
      chatMessages: conn.chatMessages || [],
      latestMessage: deriveLatestMessage(conn.chatMessages),
      notes: { text: conn.notes || '' },
      hasSharedContactInfo: Boolean(
        conn.profileData?.phone || conn.profileData?.email || conn.profileData?.instagram
      ),
      isManualContact: false,
    }));

    setConnections([...validConnections]);
    setIsLoading(false);
    return validConnections;
  };

  // Enrich with latest messages from Supabase
  const enrichConnections = (validConnections) => {
    if (!validConnections || validConnections.length === 0) {
      setEnriched(true);
      return;
    }
    const chatRoomIds = validConnections.map((c) => c.chatRoomId);

    loadLatestMessages(chatRoomIds)
      .then((latestMessages) => {
        setConnections((prev) =>
          prev.map((conn) => ({
            ...conn,
            latestMessage: latestMessages[conn.chatRoomId] || conn.latestMessage || null,
          }))
        );
        setEnriched(true);
      })
      .catch((err) => {
        console.error('[ANON_CHAT_LIST] Latest messages failed:', err);
        setEnrichFailed(true);
        setEnriched(true);
      });
  };

  // Load on mount
  useEffect(() => {
    const init = async () => {
      const validConnections = await loadConnectionsFromStorage();
      enrichConnections(validConnections);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle clicking on a chat
  const handleChatClick = (chatRoomId) => {
    updateSessionChatRoomId(chatRoomId);
    navigateTo('anon-connection-page', {
      source: 'chat-list',
    });
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100dvh',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          backgroundColor: '#FFFFFF',
          zIndex: 100,
        }}
      >
        <div style={{ height: 'env(safe-area-inset-top)', backgroundColor: '#FFFFFF' }} />
        <div
          style={{
            width: '100%',
            maxWidth: '600px',
            margin: '0 auto',
            boxSizing: 'border-box',
            paddingTop: '12px',
            paddingBottom: '12px',
            paddingLeft: '20px',
            paddingRight: '20px',
            textAlign: 'left',
          }}
        >
          <span
            style={{
              fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
              fontSize: '32px',
              fontWeight: '800',
              color: '#000000',
            }}
          >
            Chats
          </span>
        </div>
      </div>

      {/* Scrollable Content */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingTop: '80px',
          paddingBottom: '80px',
        }}
      >
        {isLoading || (!enriched && chatConnections.length === 0) ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>
        ) : chatConnections.length === 0 ? (
          <div
            style={{
              padding: '40px 20px',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
              paddingTop: '30vh',
              minHeight: 'calc(100vh - 160px)',
            }}
          >
            <div style={{ fontSize: '16px', color: '#666666' }}>
              {enrichFailed ? 'Trouble loading your chats — check your connection' : 'No chats yet'}
            </div>
          </div>
        ) : (
          <div
            style={{
              padding: '0 0 20px 0',
              width: '100%',
              maxWidth: '600px',
              margin: '0 auto',
              boxSizing: 'border-box',
            }}
          >
            {chatConnections.map((connection, index) => {
              const latestMessage = connection.latestMessage || null;
              const { recentMessage, isMessageFromUser } = formatMessagePreview(
                latestMessage,
                getSenderId()
              );
              const chatTime = latestMessage?.timestamp || null;

              return (
                <SwipeableConnectionCard
                  key={connection.chatRoomId}
                  connection={connection}
                  onChatClick={() => handleChatClick(connection.chatRoomId)}
                  onCardClick={() => handleChatClick(connection.chatRoomId)}
                  getFormattedLocation={formatLocationForCard}
                  showMarginBottom={index < chatConnections.length - 1}
                  recentMessage={recentMessage}
                  isMessageFromUser={isMessageFromUser}
                  isManualContact={false}
                  hasUnreadMessage={hasUnreadMessages(
                    latestMessage ? [latestMessage] : [],
                    connection.chatRoomId
                  )}
                  displayTimestamp={formatRelativeTime(chatTime)}
                  variant="chat"
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnonChatListPage;
